import React, { useEffect, useState } from "react";
import api from "../../api/api";
import Spinner from "../../components/ui/Spinner";
import MessageBox from "../../components/ui/MessageBox";
import Heading from "../../components/ui/Heading";
import Button from "../../components/ui/Button";
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle } from "lucide-react";

function Cancelpage({ setView, user }) {
  const [cancelList, setCancelList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("card"); // table or card

  useEffect(() => {
    const fetchCancelList = async () => {
      try {
        const response = await api.CancelListAPI(user);
        const data = response?.data || response;
        const list = data?.Table || [];
        setCancelList(list);
        setFilteredList(list);
      } catch (err) {
        setError("Failed to fetch cancelled documents.");
        setCancelList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCancelList();
  }, [user]);

  useEffect(() => {
    const filtered = cancelList.filter(
      (item) =>
        item.CancelBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.DocType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.CanDocNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.PrimeNo?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredList(filtered);
  }, [searchTerm, cancelList]);

  return (
    <div className="w-full min-h-screen bg-gray-100">

      {/* Top Row: Heading */}
      <div className="px-3 py-3  bg-gray">
        <Heading>Cancelled Documents</Heading>
      </div>

      <div className="flex items-center justify-between px-3 py-2  bg-gray">

        <Button
          className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
      rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
          onClick={() => setView("CancelNew")}
        >
          <PlusCircle size={18} />
          <span className="font-medium">New</span>
        </Button>

        <div className="flex gap-2">
           <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
              </svg>
            </button>

            
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
        </div>
      </div>


      <div className="flex items-center gap-2 px-3 py-2  bg-gray">
        <Button
          onClick={() => setView("home")}
          className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-200 rounded-full"
        >
          ⬅
        </Button>

        <SearchInput
          type="text"
          placeholder="Search by Cancel By, Doc Type, Doc No, Prime No..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Content */}
      <div className="p-3">
        {loading ? (
          <Spinner />
        ) : error ? (
          <MessageBox message={error} />
        ) : filteredList.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No cancelled documents found.
          </div>
        ) : viewMode === "table" ? (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Cancel By
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Doc Type
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Cancel Doc No
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Cancel Doc Date
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Prime No
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredList.map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2">{item.CancelBy}</td>
                    <td className="px-4 py-2">{item.DocType}</td>
                    <td className="px-4 py-2 text-blue-600 font-medium">
                      {item.CanDocNo}
                    </td>
                    <td className="px-4 py-2">
                      {item.CanDocDate
                        ? new Date(item.CanDocDate).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="px-4 py-2">{item.PrimeNo}</td>
                    <td className="px-4 py-2  text-gray-800">{item.Notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredList.map((item, idx) => (
              <div
                key={idx}
                className="p-3 bg-white border rounded-lg shadow-sm hover:shadow-md transition"
              >
                <div className="mb-2">
                  <div className="text-sm font-semibold text-gray-800">
                    {item.CanDocNo}
                  </div>
                  <div className="text-sm text-gray-600">{item.DocType}</div>
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Cancel By:</strong> {item.CancelBy}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Date:</strong>{" "}
                  {item.CanDocDate
                    ? new Date(item.CanDocDate).toLocaleDateString("en-GB")
                    : "-"}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Prime No:</strong> {item.PrimeNo}
                </div>
              
              </div>
            ))} 
          </div>
        )}
      </div>
    </div>
  );
}

export default Cancelpage;
